/**
 * CliniSpecs — Product Video Autoplay  v6
 *
 * v2: playVideoWhenReady() calls play() immediately (preload="none" fix).
 * v3: poster-hold via cs-video-playing on deferred-media.
 * v4: white poster background on .media-poster during buffering.
 * v5: GPU-compositing fix — video opacity:0 + immediate buffering overlay.
 * v6: Gallery-wide white canvas fix for Safari / Shopify editor.
 *
 * ROOT CAUSE OF GREY GALLERY CANVAS (v6)
 * The theme CSS drives ALL gallery element backgrounds via CSS variable:
 *   .media-gallery__viewer  { background-color: var(--gallery-bg-color); }
 *   .media-gallery deferred-media { background-color: var(--gallery-bg-color); }
 *   .media-gallery iframe, .media-gallery video { background-color: var(--gallery-bg-color); }
 *   .media-thumbs__btn { background-color: var(--gallery-bg-color); }
 *
 * The variable is set in JavaScript via an inline style on the <media-gallery>
 * custom element: --gallery-bg-color: #ffffff. In Chrome, this resolves
 * correctly and the gallery is white. In Safari and the Shopify editor,
 * CSS custom properties on custom elements may not be resolved when the
 * browser first paints, causing the variable to fall back to its initial
 * (unresolved) value — typically transparent or grey — making the entire
 * product media canvas appear grey, including behind normal product images.
 *
 * This explains why:
 * - Product images appear to sit on a grey background in Safari/editor.
 * - Chrome DOM inspection shows white (variable resolves correctly).
 * - The issue is independent of the video-specific fix.
 *
 * FIX (v6)
 * Inject explicit background-color: #fff !important on all gallery canvas
 * elements without relying on var(--gallery-bg-color). Scoped strictly to
 * the .media-gallery context — no global site background impact.
 *
 * VIDEO-SPECIFIC FIX (v5, preserved)
 * - cs-video-buffering: immediate white overlay on first click.
 * - GPU-promoted ::before via will-change:opacity.
 * - video opacity:0 while buffering.
 * - All video CSS scoped to [data-media-type="video"].
 */

(function () {
  'use strict';

  /* 1. CSS */
  var style = document.createElement('style');
  style.id = 'cs-video-style';
  style.textContent = [

    /* ── GALLERY CANVAS FIX (v6) ─────────────────────────────────────────────────────',
       Explicit #fff — does not rely on var(--gallery-bg-color).',
       Fixes grey canvas in Safari / Shopify editor where the CSS variable',
       on <media-gallery> custom element is not resolved at paint time.',
       Scoped to .media-gallery — no global background impact. */
    '.media-gallery__viewer { background-color: #fff !important; }',
    '.media-gallery .media-viewer__item { background-color: #fff !important; }',
    '.media-gallery .media { background-color: #fff !important; }',
    '.media-gallery deferred-media { background-color: #fff !important; }',
    '.media-gallery iframe { background-color: #fff !important; }',
    '.media-gallery video { background-color: #fff !important; }',
    '.media-thumbs__btn { background-color: #fff !important; }',

    /* ── VIDEO ITEMS BACKGROUND (v4+) ────────────────────────────────────*/
    '.media-gallery .media-viewer__item[data-media-type="video"] { background:#fff !important; }',
    '.media-gallery .media-viewer__item[data-media-type="video"] deferred-media { background:#fff !important; }',

    /* ── BUFFERING OVERLAY (v5) ──────────────────────────────────────────────────────',
       GPU-promoted ::before on deferred-media.',
       top/right/bottom/left as Safari fallback for inset shorthand. */
    '.media-gallery .media-viewer__item[data-media-type="video"].cs-video-buffering deferred-media::before,',
    '.media-gallery .media-viewer__item[data-media-type="video"].cs-video-playing deferred-media::before {',
    '  content: "";',
    '  position: absolute;',
    '  top: 0; right: 0; bottom: 0; left: 0;',
    '  background: #fff;',
    '  z-index: 10;',
    '  pointer-events: none;',
    '  will-change: opacity;',
    '  transition: opacity 0.3s ease;',
    '}',
    '.media-gallery .media-viewer__item[data-media-type="video"].cs-video-buffering deferred-media::before { opacity: 1; }',
    '.media-gallery .media-viewer__item[data-media-type="video"].cs-video-playing deferred-media::before { opacity: 0; }',

    /* ── VIDEO OPACITY (v5) ──────────────────────────────────────────────────────*/
    '.media-gallery .media-viewer__item[data-media-type="video"] deferred-media[loaded] video {',
    '  opacity: 0 !important;',
    '  transition: opacity 0.3s ease;',
    '}',
    '.media-gallery .media-viewer__item[data-media-type="video"].cs-video-playing deferred-media[loaded] video {',
    '  opacity: 1 !important;',
    '  transition: opacity 0.3s ease;',
    '}',

    /* ── POSTER HOLD (v4+) ───────────────────────────────────────────────────────*/
    '.media-gallery .media-viewer__item[data-media-type="video"] deferred-media[loaded] .media-poster {',
    '  background: #fff !important;',
    '  background-color: #fff !important;',
    '  opacity: 1 !important;',
    '  pointer-events: none !important;',
    '  transition: opacity 0.3s ease !important;',
    '}',
    '.media-gallery .media-viewer__item[data-media-type="video"].cs-video-playing deferred-media[loaded] .media-poster {',
    '  opacity: 0 !important;',
    '  pointer-events: none !important;',
    '}',

    /* ── CONTROLS + INTERACTION ──────────────────────────────────────────────────*/
    'video::-webkit-media-controls          { display:none !important; }',
    'video::-webkit-media-controls-enclosure{ display:none !important; }',
    'video::-webkit-media-controls-panel    { display:none !important; }',
    'video { pointer-events:none; }',

    /* ── SIDEBAR THUMB ───────────────────────────────────────────────────────────────────*/
    '.media-thumbs__btn:has(.media-thumbs__badge) img.product-image { opacity:0; transition:opacity .15s ease; }',

  ].join('\n');
  document.head.appendChild(style);

  /* 2. State helpers */
  function setBuffering(mediaItem) {
    if (!mediaItem) return;
    mediaItem.classList.add('cs-video-buffering');
    mediaItem.classList.remove('cs-video-playing');
  }

  function setPlaying(dm, mediaItem) {
    if (dm) dm.classList.add('cs-video-playing');
    if (mediaItem) {
      mediaItem.classList.add('cs-video-playing');
      mediaItem.classList.remove('cs-video-buffering');
    }
  }

  /* 3. Core video helpers */
  function prepareVideo(video) {
    video.muted       = true;
    video.playsInline = true;
    video.autoplay    = true;
    video.loop        = true;
    video.controls    = false;
    video.removeAttribute('controls');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
  }

  function attachPlayingListener(video) {
    var dm = video.closest('deferred-media');
    if (!dm) return;
    var mediaItem = dm.closest('.media-viewer__item');
    function onPlaying() { setPlaying(dm, mediaItem); }
    video.removeEventListener('playing', video._csOnPlaying);
    video._csOnPlaying = onPlaying;
    video.addEventListener('playing', onPlaying);
    video.removeEventListener('canplay', video._csOnCanPlay);
    video._csOnCanPlay = function () { if (!video.paused) onPlaying(); };
    video.addEventListener('canplay', video._csOnCanPlay);
  }

  function playVideo(video) {
    prepareVideo(video);
    var dm = video.closest('deferred-media');
    var mediaItem = dm ? dm.closest('.media-viewer__item') : null;
    if (dm) dm.classList.remove('cs-video-playing');
    setBuffering(mediaItem);
    attachPlayingListener(video);
    var p = video.play();
    if (p && typeof p.catch === 'function') { p.catch(function () {}); }
  }

  function playVideoWhenReady(video) { prepareVideo(video); playVideo(video); }

  /* 4. Patch DeferredMedia.loadContent */
  function patchDeferredMedia() {
    var proto = window.customElements.get('deferred-media');
    if (!proto || !proto.prototype) return false;
    if (proto.prototype._csLoadPatched) return true;
    proto.prototype._csLoadPatched = true;
    var originalLoad = proto.prototype.loadContent;
    proto.prototype.loadContent = function (focus, pause, trigger) {
      var wasLoaded = this.hasAttribute('loaded');
      if (!wasLoaded) {
        var mi = this.closest('.media-viewer__item');
        if (mi && mi.dataset.mediaType === 'video') { setBuffering(mi); }
      }
      originalLoad.call(this, focus, pause, trigger);
      var self = this;
      requestAnimationFrame(function () {
        var poster = self.querySelector('.media-poster, .js-load-media');
        if (poster) { poster.style.pointerEvents = 'none'; poster.setAttribute('aria-hidden', 'true'); }
        self.querySelectorAll('video').forEach(function (video) {
          if (wasLoaded) { try { video.currentTime = 0; } catch (e) {} }
          playVideoWhenReady(video);
        });
      });
    };
    return true;
  }

  /* 5. Patch MediaGallery */
  function patchMediaGallery() {
    var mg = document.querySelector('media-gallery');
    if (!mg) return false;
    var MGClass = mg.constructor;
    if (!MGClass || !MGClass.playActiveMedia) return false;
    if (MGClass._csPlayPatched) return true;
    MGClass._csPlayPatched = true;
    var origPlay = MGClass.playActiveMedia;
    MGClass.playActiveMedia = function (mediaItem) {
      if (!mediaItem) return origPlay.call(this, mediaItem);
      if (mediaItem.dataset.mediaType === 'video') { setBuffering(mediaItem); }
      origPlay.call(this, mediaItem);
      requestAnimationFrame(function () {
        var dm = mediaItem.querySelector('deferred-media');
        if (!dm) return;
        var poster = dm.querySelector('.media-poster, .js-load-media');
        if (poster) { poster.style.pointerEvents = 'none'; poster.setAttribute('aria-hidden', 'true'); }
        dm.querySelectorAll('video').forEach(function (video) {
          try { video.currentTime = 0; } catch (e) {}
          playVideoWhenReady(video);
        });
      });
    };
    var proto = Object.getPrototypeOf(mg);
    if (!proto._csActivePatched) {
      proto._csActivePatched = true;
      var origCSAM = proto.customSetActiveMedia;
      proto.customSetActiveMedia = function (mediaItem, scroll) {
        if (mediaItem) this._csLastItem = mediaItem;
        if (mediaItem && mediaItem.dataset.mediaType === 'video') { setBuffering(mediaItem); }
        origCSAM.call(this, mediaItem, scroll);
        if (!mediaItem || mediaItem.dataset.mediaType !== 'video') return;
        var dm = mediaItem.querySelector('deferred-media');
        if (!dm) return;
        requestAnimationFrame(function () {
          var poster = dm.querySelector('.media-poster, .js-load-media');
          if (poster) { poster.style.pointerEvents = 'none'; poster.setAttribute('aria-hidden', 'true'); }
          dm.querySelectorAll('video').forEach(function (video) {
            try { video.currentTime = 0; } catch (e) {}
            playVideoWhenReady(video);
          });
        });
      };
    }
    return true;
  }

  /* 6. MutationObserver */
  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.tagName === 'VIDEO') { playVideoWhenReady(node); }
        else if (node.querySelectorAll) { node.querySelectorAll('video').forEach(playVideoWhenReady); }
      });
    });
  }).observe(document.body, { childList: true, subtree: true });

  function patchAllVideos() { document.querySelectorAll('video').forEach(playVideoWhenReady); }

  /* 7. Scroll-reset patch */
  function applyScrollPatch() {
    var mg = document.querySelector('media-gallery');
    if (!mg || !mg.viewer) return false;
    var proto = Object.getPrototypeOf(mg);
    if (proto._csScrollPatched) return true;
    proto._csScrollPatched = true;
    var checks = 0;
    var intervalId = setInterval(function () {
      checks++; if (checks > 200) { clearInterval(intervalId); return; }
      var sel = mg._csLastItem || mg._userSelectedItem;
      if (mg.viewer.scrollLeft === 0 && sel && sel.offsetLeft > 0 && mg.currentItem === sel) {
        mg.currentItem = null; mg.customSetActiveMedia(sel, true);
      }
    }, 300);
    return true;
  }

  /* 8. Video thumbnail preview fix */
  function fixVideoThumbnails() {
    var mg = document.querySelector('media-gallery');
    if (!mg) return false;
    var allThumbItems = Array.from(mg.querySelectorAll('.media-thumbs__item'));
    var sourceImg = null;
    for (var i = 0; i < allThumbItems.length; i++) {
      var li = allThumbItems[i]; if (li.offsetWidth === 0) continue;
      var mid = li.dataset.mediaId; if (!mid) continue;
      var vi = mg.querySelector('.media-viewer__item[data-media-id="' + mid + '"]');
      if (!vi || vi.dataset.mediaType !== 'image') continue;
      var img = li.querySelector('img');
      if (img && img.src && img.src.indexOf('data:') !== 0) { sourceImg = img; break; }
    }
    if (!sourceImg) return false;
    var applied = false;
    allThumbItems.forEach(function (li) {
      if (li.offsetWidth === 0) return;
      var mid = li.dataset.mediaId; if (!mid) return;
      var vi = mg.querySelector('.media-viewer__item[data-media-id="' + mid + '"]');
      if (!vi || vi.dataset.mediaType !== 'video') return;
      var img = li.querySelector('img'); if (!img) return;
      if (img.src !== sourceImg.src) { img.src = sourceImg.src; img.srcset = sourceImg.srcset || ''; img.sizes = sourceImg.sizes || ''; applied = true; }
      if (img.style.opacity !== '1') { img.style.opacity = '1'; img.style.transition = 'opacity .15s ease'; applied = true; }
    });
    return applied;
  }
  function waitAndFixThumbs() {
    if (fixVideoThumbnails()) return;
    var a = 0; var iv = setInterval(function () { if (fixVideoThumbnails() || ++a > 60) clearInterval(iv); }, 100);
  }

  /* 9. Boot */
  function boot() {
    if (!patchDeferredMedia()) { var a=0; var p=setInterval(function(){if(patchDeferredMedia()||++a>50)clearInterval(p);},100); }
    if (!patchMediaGallery()) { var b=0; var q=setInterval(function(){if(patchMediaGallery()||++b>50)clearInterval(q);},100); }
    if (!applyScrollPatch()) { var c=0; var r=setInterval(function(){if(applyScrollPatch()||++c>50)clearInterval(r);},100); }
    patchAllVideos();
    waitAndFixThumbs();
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); } else { boot(); }
  window.addEventListener('load', function () { patchAllVideos(); waitAndFixThumbs(); });
  document.addEventListener('on:variant:change', function () { setTimeout(function () { waitAndFixThumbs(); patchMediaGallery(); }, 400); });

})();
